import { useState, useRef, useEffect, useCallback, type KeyboardEvent } from 'react';
import type { SurveyMetadata } from '@/types/survey';

/**
 * Return interface for the useSurveyMetadata hook.
 */
interface UseSurveyMetadataReturn {
  startQuestionTimer: () => void;
  stopQuestionTimer: (index: number) => void;
  trackAnswerChange: (questionId: string, hasExistingAnswer: boolean) => void;
  trackKeyInteraction: (e: KeyboardEvent<HTMLElement>) => void;
  getFinalMetadata: (totalQuestions: number) => SurveyMetadata;
}

/**
 * Hook to track user interaction metadata during the survey.
 * Handles timing, device detection, and interaction counts.
 *
 * @returns {UseSurveyMetadataReturn} Metadata tracking methods.
 */
export const useSurveyMetadata = (): UseSurveyMetadataReturn => {
  // Track start time of the current active question
  const questionStartTimeRef = useRef<number | null>(null);

  // Accumulate total milliseconds spent per question index
  const timeAccumulatorRef = useRef<Record<number, number>>({});

  // Track number of times an answer was changed per question ID
  const answerChangesRef = useRef<Record<string, number>>({});

  // Track total backspaces (for typing metrics)
  const backspaceCountRef = useRef<number>(0);

  // Track start time of the entire survey session
  const surveyStartTimeRef = useRef<number>(Date.now());

  // Device type state
  const [deviceType, setDeviceType] = useState<'mobile' | 'tablet' | 'desktop'>('desktop');

  useEffect(() => {
    const updateDeviceType = () => {
      const width = window.innerWidth;
      if (width < 768) setDeviceType('mobile');
      else if (width < 1024) setDeviceType('tablet');
      else setDeviceType('desktop');
    };

    updateDeviceType();
    window.addEventListener('resize', updateDeviceType);
    return () => window.removeEventListener('resize', updateDeviceType);
  }, []);

  /**
   * Start the timer for a specific question session.
   * Call when a question becomes active.
   */
  const startQuestionTimer = useCallback(() => {
    questionStartTimeRef.current = Date.now();
  }, []);

  /**
   * Stop the timer for a specific question index and accumulate time.
   * Call when navigating away from a question.
   */
  const stopQuestionTimer = useCallback((index: number) => {
    if (questionStartTimeRef.current !== null) {
      const duration = Date.now() - questionStartTimeRef.current;
      timeAccumulatorRef.current[index] = (timeAccumulatorRef.current[index] || 0) + duration;
      questionStartTimeRef.current = null;
    }
  }, []);

  /**
   * Track an answer change event.
   * Only increments if the question already had an answer (indicating a change).
   */
  const trackAnswerChange = useCallback((questionId: string, hasExistingAnswer: boolean) => {
    if (hasExistingAnswer) {
      answerChangesRef.current[questionId] = (answerChangesRef.current[questionId] || 0) + 1;
    }
  }, []);

  /**
   * Track keyboard interactions (e.g., backspaces).
   *
   * @param e - The React KeyboardEvent.
   */
  const trackKeyInteraction = useCallback((e: KeyboardEvent<HTMLElement>) => {
    if (e.key === 'Backspace') {
      backspaceCountRef.current += 1;
    }
  }, []);

  /**
   * Compile all tracked data into the final metadata object.
   */
  const getFinalMetadata = useCallback(
    (totalQuestions: number): SurveyMetadata => {
      // Generate ordered array of times matching question indices
      const timePerQuestionArray = Array.from({ length: totalQuestions }).map(
        (_, i) => timeAccumulatorRef.current[i] || 0
      );

      const totalTimeSeconds = Math.floor((Date.now() - surveyStartTimeRef.current) / 1000);

      return {
        time_per_question: timePerQuestionArray,
        total_time: totalTimeSeconds,
        answer_changes: answerChangesRef.current,
        backspace_count: backspaceCountRef.current,
        typing_speed: 0, // Placeholder: requires text length context to calculate
        first_option_bias: 0, // Placeholder
        time_of_day: new Date().toISOString(),
        device_type: deviceType,
      };
    },
    [deviceType]
  );

  return {
    startQuestionTimer,
    stopQuestionTimer,
    trackAnswerChange,
    trackKeyInteraction,
    getFinalMetadata,
  };
};
